import React, {useEffect, useMemo, useRef, useState} from 'react';
import {theme, ConfigProvider, Segmented, Card, Button, message} from "antd";
import ExistingQueries from "../../../../../../tools-drawer/query/ExistingQueries";
import InstaQuery from "../../../../../../tools-drawer/query/InstaQuery";
import UploadedFileStatus from "./UploadedFileStatus";
import Omitter from "./upload-buttons/popover-items/Omitter";
import {InternalNote} from "../../../../../_new/uploads-overview/SectionManager";
import QueryForm from "../../../../../../tools-drawer/query/QueryForm";
import ReasonForAdvisorReview from "./upload-buttons/popover-items/ReasonForAdvisorReview";
import {performGQL} from "../../../../../../../api-functions/functions";


// Same generic productAPI mutation apps/admin/src/app/providers/mortgage/
// uploads/verified-upload-sections/account-statement/BalancesForm.jsx's own
// "Fill from statement" button dispatches through (see that file's own
// header comment) - action routes server-side (dispatchers/entry.js's own
// ROUTES table) to product7package7task7upload/commands/
// extract-statement-summary.js.
const productAPIMutation = /* GraphQL */ `
    mutation ProductAPI($action: String!, $payload: AWSJSON) {
        productAPI(action: $action, payload: $payload)
    }
`;


function UploadAnnotator({uploadID, section, url, close, setMortgage}) {

    const upload = section?.uploads.find(it => it.id === uploadID);

    const initialState = upload?.props?.upload?.status === 'PENDING' ? 'status' : 'info'

    const [activeTab, setActiveTab] = useState(initialState);

    const memoSection = useMemo(() => {
        return section || {};
    }, [section]);

    const {  BalancesForm } = section || {};

    // Both deps were [] before - froze section (and everything downstream,
    // e.g. InstaVerifiedDataPoint/MyLittleForm's own upload.verifiedData)
    // to whatever it was at first mount, so a live subscription update from
    // MortgageProvider (e.g. extractStatementSummary's own iban/dates
    // landing) never reached an already-open UploadAnnotator - only visible
    // again after closing and reopening the popout (a fresh mount).
    // BalancesForm never had this problem since it reads straight from
    // useMortgage() on every render instead of through this memo chain.
    const MemoUploadSectionProps = useMemo(() => {
        return {
            uploadID:uploadID, section:memoSection, setMortgage:setMortgage
        }
    },[uploadID, memoSection, setMortgage])
    useEffect(() => {
        if (!section){
            close()
        }
    }, [section]);
    if (!upload){
        return null
    }
    if (!section && url) {
        return <iframe
            src={url}
            style={{flex: 2, border: 'none', position:'fixed'}}
            title="PDF Viewer"
        />
    }


    const tabs = [
        {
            value: 'queries',
            label: 'Queries'
        }, {
            value: 'info',
            label: 'Info'
        },
        {
            value: 'status',
            label: 'Upload Status'
        }
    ]
    if (!!BalancesForm){
        tabs.push({
            value: 'balances',
            label: 'Balance Info'
        })
    }
    return (
        <ConfigProvider
            theme={{
                algorithm: theme.defaultAlgorithm, // ✅ forces light theme
            }}
        >


            {/* Fixed-height panes that scroll themselves - NewWindow's copyStyles
                can carry antd's scroll-lock (html body {overflow-y: hidden}) into
                the popout if a Drawer/Modal was open, so never rely on body scroll. */}
            <div style={{display: 'flex', height: '100vh', overflow: 'hidden', backgroundColor: 'white'}}>
                {url && <iframe
                    src={url}
                    style={{flex: 2, height: '100%', border: 'none'}}
                    title="PDF Viewer"
                />}
                <div style={{flex: 1, height: '100%', overflowY: 'auto', padding: 16, minWidth: 700}}>
                    <Segmented size="large" onChange={setActiveTab} value={activeTab} options={tabs}
                    />
                    {/*QUERIES AND OMITTER*/}
                    <div hidden={activeTab !== "queries"}>
                        <div className="d-col gap-15 mt-15">

                            {BalancesForm ? (
                                <InstaQuery close={close} section={section} uploadID={uploadID}/>

                            ) : <QueryForm config={{
                                referenceType: 'UPLOAD',
                                referenceId: uploadID,
                                section: section.name,
                                index: section.index,
                                stage: section.stage,
                                ownerName: section.ownerName,
                            }} /> }

                            <ExistingQueries config={{
                                stage: section.stage,
                                section: section.name,
                                index: section.index,
                                referenceId: uploadID,
                                inExtWindow: true
                            }}/>
                        </div>

                    </div>

                    {/*DATA FORM AND APPLICATION FORM*/}
                    <div hidden={activeTab !== "info"}>
                        <Card>
                            <Omitter uploadId={uploadID}/>
                        </Card>
                        <UploadInfo  {...MemoUploadSectionProps}/>

                        {/*<Collapse items={[{*/}
                        {/*    key: 1,*/}
                        {/*    label: 'Old Data Form',*/}
                        {/*    children: DataForm*/}
                        {/*}*/}
                        {/*]}/>*/}
                    </div>

                    {/*FILE STATUS*/}
                    <div hidden={activeTab !== "status"}>

                        <UploadedFileStatus section={section} uploadId={uploadID} />
                    </div>
                    {BalancesForm && (
                        <div hidden={activeTab !== "balances"}>
                            <CardWithSpacing title="Balance Info" >
                                <BalancesForm uploadID={uploadID}/>
                            </CardWithSpacing>
                        </div>
                    )}

                </div>
            </div>
        </ConfigProvider>
    );
}


function UploadInfo({section, uploadID, setMortgage}) {

    const { DataForm, BalancesForm, ApplicationForm } = useMemo(() => {
        return section || {};
    }, [section.DataForm, section.BalancesForm, section.ApplicationForm]);

    // custom setMortgage
    const updatingFunctions = useRef(null)
    // const updateTimer = useRef(null)
    const doOutstandingUpdates = () => {
        if (updatingFunctions.current ) {

            setMortgage(s=>{
                let newS = {...s}
                if (Array.isArray(updatingFunctions.current)){
                    updatingFunctions.current.forEach(fn => {
                        console.log('Running updater function', fn)
                        newS = fn(newS)
                    });
                }
                return newS;
            })
        }
    }
    // const startUpdateTimer = () => {
    //     if (updateTimer.current) {
    //         clearTimeout(updateTimer.current);
    //     }
    //     updateTimer.current = setTimeout(doOutstandingUpdates, 5000);
    // }
    const customSetMortgage = (updater=>{
        if (!updatingFunctions.current) {
            updatingFunctions.current = [];
        }
        updatingFunctions.current.push(updater);
        // startUpdateTimer()
    })
    useEffect(()=>{

        return () => {
            // if (updateTimer.current) {
            //     clearTimeout(updateTimer.current);
            // }
            doOutstandingUpdates()
            updatingFunctions.current = null; // Clear the updater functions
        }
    },[])


    // section was missing from these deps - see UploadAnnotator's own
    // MemoUploadSectionProps comment above for why that made this upload
    // (and DataForm/InstaVerifiedDataPoint's own read of its verifiedData)
    // stale to any live update.
    const upload = useMemo(()=>section.uploads.find(it => it.id === uploadID), [section, uploadID]);

    // TEMPORARY - manual trigger for extractStatementSummary
    // (product7package7task7upload/commands/extract-statement-summary.js).
    // Fire-and-forget - saves IBAN/start date/end date as this upload's own
    // verified data, and (write-once) joint/currency onto the
    // UserApplicantCurrentAccount row this upload's own `index` points at.
    // Remove once real summary-extraction UI (triggered automatically,
    // rather than by hand) replaces this.
    const [extractingSummary, setExtractingSummary] = useState(false);
    const extractSummary = async () => {
        setExtractingSummary(true);
        try {
            await performGQL({
                action: 'extractStatementSummary',
                payload: JSON.stringify({uploadID}),
            }, productAPIMutation);
            message.info('Reading the statement - IBAN/dates/account info will fill in automatically once ready');
        } catch (error) {
            console.error('extractStatementSummary failed', error);
            message.error(error.message || 'Could not read the statement summary');
        } finally {
            setExtractingSummary(false);
        }
    };

    return (
        <>
            <Button size="small" loading={extractingSummary} onClick={extractSummary} className="mb-15">
                Debug: extractStatementSummary
            </Button>

            {DataForm &&
                <CardWithSpacing title="Upload Info"> <DataForm upload={upload} inExtWindow={true} setMortgage={customSetMortgage} />
                </CardWithSpacing>}
            {ApplicationForm &&
                <CardWithSpacing title="Application Info" > <ApplicationForm section={section}/>
                </CardWithSpacing>}
            <Card title="Section Notes"  className="my-15"  styles={{body: {paddingLeft: 12, paddingRight:12}}} >
                <InternalNote variant="borderless" section={section}/>
            </Card>
            {upload.props.upload.status === 'REVIEWING' && (
                <div className="mt-15">
                    <p className="m-0">What must the advisor look at on this upload?</p>
                    <ReasonForAdvisorReview uploadId={uploadID}/>
                </div>
            )}
        </>
    )
}
function CardWithSpacing({children, title}) {
    return <Card title={title} children={children} className="my-15"
                 styles={{body: {display: 'flex', flexDirection: 'column', gap: 10}}}/>
}


export default UploadAnnotator;